import type { FilterConfig, FilterState } from '@/components/filter-bar';
import { TableFilters } from '@/components/filter-bar';
import { CLIENT_CATEGORISATIONS, type ClientCategorisation } from '@/services/api/client-categorisation';
import { capitalizeLower } from '@/utils/string';

// The shared FilterEditDialog's `select` type renders raw `options` strings as both the
// dropdown value AND its display label — so the lowercase wire values can't be used directly
// as `options` without regressing the dropdown copy from "Retail"/"Professional" to
// the raw lowercase enum. Map to/from the display labels instead, keeping the exported
// `onChange` on the real ClientCategorisation wire values.
const CATEGORISATION_OPTIONS = CLIENT_CATEGORISATIONS.map((value) => capitalizeLower(value));
const LABEL_TO_CATEGORISATION = new Map<string, ClientCategorisation>(CLIENT_CATEGORISATIONS.map((value) => [capitalizeLower(value), value]));

/**
 * Server-side filtering only (own comment, preserved below): `getValue` is never invoked —
 * TableFilters only calls it through `deriveFilterOptions`, and this column ships a static
 * `options` list, so that path is unreachable.
 */
const CATEGORISATION_FILTER_CONFIGS: Array<FilterConfig<unknown>> = [
  { getValue: () => undefined, key: 'categorisation', options: CATEGORISATION_OPTIONS, title: 'Categorisation', type: 'select' },
];

interface ClientCategorisationFilterBarProps {
  value?: ClientCategorisation;
  onChange: (value?: ClientCategorisation) => void;
}

/**
 * Filter chip row, now the shared filter-bar module: static
 * Filters chip, one chip for the only filterable column (Categorisation, server-side select
 * filter), Clear All when active. Applying/clearing re-queries the server — no client-side
 * filtering.
 */
export const ClientCategorisationFilterBar = ({ value, onChange }: ClientCategorisationFilterBarProps) => {
  const state: FilterState = value ? { categorisation: [capitalizeLower(value)] } : {};

  const handleChange = (next: FilterState) => {
    const label = next.categorisation?.[0];
    onChange(label ? LABEL_TO_CATEGORISATION.get(label) : undefined);
  };

  return <TableFilters configs={CATEGORISATION_FILTER_CONFIGS} onChange={handleChange} rows={[]} state={state} />;
};
ClientCategorisationFilterBar.displayName = 'ClientCategorisationFilterBar';
